import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
import NotFound from "./pages/NotFound";
const MapPage = lazy(() => import("./pages/Map"));
const VirtualToursPage = lazy(() => import("./pages/VirtualTours"));
const RoutesPage = lazy(() => import("./pages/Routes"));
const AgendaPage = lazy(() => import("./pages/Agenda"));
import ErrorBoundary from "@/components/ErrorBoundary";
import { MunicipalityProvider, useMunicipalityContext } from "@/contexts/MunicipalityContext";
import { URLConfigProvider, useURLConfigContext } from "@/contexts/URLConfigContext";
import { Screensaver } from "@/components/Screensaver";
import { useInactivity } from "@/hooks/useInactivity";
import { AndroidOptimizer } from "@/components/AndroidOptimizer";
import { RemoteReloadListener } from "@/components/RemoteReloadListener";
import { useMemoryMonitor } from "@/hooks/useMemoryMonitor";
import { usePerformanceMonitor } from "@/hooks/usePerformanceMonitor";
import { setLoggerMunicipalityId, logToRemote } from "@/utils/remoteLogger";

import { TouchCoordinateFixer } from "@/components/TouchCoordinateFixer";
import { MediaWarmup } from '@/components/MediaWarmup';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: 1000,
      staleTime: 2 * 60 * 1000, // 2 minutos (reducido para memoria)
      gcTime: 5 * 60 * 1000, // 5 minutos (reducido)
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

// Limpieza periódica de caché cada 5 minutos
setInterval(() => {
  const now = Date.now();
  queryClient.getQueryCache().getAll().forEach((query) => {
    const dataUpdatedAt = query.state.dataUpdatedAt;
    if (dataUpdatedAt && now - dataUpdatedAt > 5 * 60 * 1000) {
      queryClient.removeQueries({ queryKey: query.queryKey });
    }
  });
}, 5 * 60 * 1000);

// Array de eventos de inactividad fuera del componente para mantener referencia estable
const INACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart'];

// Componente interno que muestra loading o contenido
const AppLoader = () => {
  const config = useURLConfigContext();

  if (config.isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return <AppContent />;
};

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useURLConfigContext();
  const { municipalityId, setMunicipalityId } = useMunicipalityContext();
  const [isWebViewOpen, setIsWebViewOpen] = useState(false);

  // Monitoring hooks
  usePerformanceMonitor();

  const memoryStats = useMemoryMonitor({
    checkInterval: 30000,
    warningThreshold: 0.75,
    criticalThreshold: 0.85,
    onWarning: (stats) => {
      logToRemote('memory_warning', {
        usagePercent: Math.round(stats.usagePercent * 100),
        usedMB: Math.round(stats.usedJSHeapSize / 1024 / 1024),
        limitMB: Math.round(stats.jsHeapSizeLimit / 1024 / 1024)
      }, 'warn');
    },
    onCritical: (stats) => {
      logToRemote('memory_critical', {
        usagePercent: Math.round(stats.usagePercent * 100),
        usedMB: Math.round(stats.usedJSHeapSize / 1024 / 1024),
        limitMB: Math.round(stats.jsHeapSizeLimit / 1024 / 1024)
      }, 'critical');

      // Limpiar caché agresivamente
      console.warn('🧹 Limpiando caché por memoria crítica...');
      queryClient.clear();
    },
    enabled: true
  });

  useEffect(() => {
    if (config.municipalityId) {
      setMunicipalityId(config.municipalityId);
    }
  }, [config.municipalityId, setMunicipalityId]);

  // Configurar municipality ID para el logger y loggear app started
  useEffect(() => {
    if (municipalityId) {
      setLoggerMunicipalityId(municipalityId);
      logToRemote('app_started', {
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        memory: (performance as any).memory ? {
          limitMB: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024)
        } : 'not_available'
      }, 'info');
    }
  }, [municipalityId]);

  useEffect(() => {
    const handleWebViewOpen = () => {
      console.log('🌐 WebView abierto - deshabilitando useInactivity');
      setIsWebViewOpen(true);
    };
    const handleWebViewClose = () => {
      console.log('🌐 WebView cerrado - habilitando useInactivity');
      setIsWebViewOpen(false);
    };
    const handleForceScreensaver = () => {
      console.log('💤 Forzando activación del salvapantallas');
      setScreensaverActive(true);
    };

    window.addEventListener('webViewOpen', handleWebViewOpen);
    window.addEventListener('webViewClose', handleWebViewClose);
    window.addEventListener('forceScreensaver', handleForceScreensaver);

    return () => {
      window.removeEventListener('webViewOpen', handleWebViewOpen);
      window.removeEventListener('webViewClose', handleWebViewClose);
      window.removeEventListener('forceScreensaver', handleForceScreensaver);
    };
  }, []);

  const shouldActivateScreensaver = location.pathname === '/';

  const { isInactive, resetInactivity } = useInactivity({
    timeout: 30000,
    events: INACTIVITY_EVENTS,
    enabled: shouldActivateScreensaver && !isWebViewOpen,
  });
  const [screensaverActive, setScreensaverActive] = useState(false);
  const [isClosingScreensaver, setIsClosingScreensaver] = useState(false);

  useEffect(() => {
    if (shouldActivateScreensaver) {
      setScreensaverActive(isInactive);
    } else {
      setScreensaverActive(false);
    }
  }, [isInactive, shouldActivateScreensaver]);

  const handleScreensaverClose = () => {
    setIsClosingScreensaver(true);

    // Cerrar cualquier WebView abierto
    window.dispatchEvent(new Event('closeWebView'));

    // Navegar a inicio preservando los query params existentes
    navigate({ pathname: '/', search: window.location.search });

    setTimeout(() => {
      setScreensaverActive(false);
      resetInactivity();
      setIsClosingScreensaver(false);
    }, 300);
  };

  return (
    <div className={screensaverActive || isClosingScreensaver ? 'pointer-events-blocked' : ''}>
      <AndroidOptimizer />
      {municipalityId && <MediaWarmup municipalityId={municipalityId} />}
      {municipalityId && <RemoteReloadListener municipalityId={municipalityId} />}
      <TouchCoordinateFixer />
      <Toaster />
      <Sonner />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/auth" element={<Suspense fallback={<div className="loading-screen"><div className="loading-spinner" /></div>}><Auth /></Suspense>} />
        <Route path="/admin" element={<Suspense fallback={<div className="loading-screen"><div className="loading-spinner" /></div>}><Admin /></Suspense>} />
        <Route path="/map" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-muted-foreground">Cargando mapa...</div>}>
            <MapPage />
          </Suspense>
        } />
        <Route path="/visitas-360" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-muted-foreground">Cargando visitas 360...</div>}>
            <VirtualToursPage />
          </Suspense>
        } />
        <Route path="/rutas" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-muted-foreground">Cargando rutas...</div>}>
            <RoutesPage />
          </Suspense>
        } />
        <Route path="/agenda" element={
          <Suspense fallback={<div className="flex items-center justify-center h-screen text-muted-foreground">Cargando agenda...</div>}>
            <AgendaPage />
          </Suspense>
        } />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {shouldActivateScreensaver && (
        <Screensaver
          municipalityId={municipalityId}
          isActive={screensaverActive}
          onInteraction={handleScreensaverClose}
        />
      )}
    </div>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <URLConfigProvider>
        <MunicipalityProvider>
          <TooltipProvider>
            <BrowserRouter>
              <AppLoader />
            </BrowserRouter>
          </TooltipProvider>
        </MunicipalityProvider>
      </URLConfigProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
